import clsx from 'clsx'
import { type PropsWithChildren, type ReactNode, useRef } from 'react'
import { useToggle } from 'usehooks-ts'

import { FvLinkButton, Icon, type IconName } from '@fv/client-components'
import type { PropsWithClassName } from '@fv/client-types'

import { PillHeader } from '@/components/PillHeader'

type FormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

export type BaseShipmentFormSectionProps = {
  collapsible?: boolean
  defaultCollapsed?: boolean
}
export const pickFormSectionProps = (props: BaseShipmentFormSectionProps) => {
  return {
    collapsible: props.collapsible,
    defaultCollapsed: props.defaultCollapsed,
  }
}

export type ShipmentFormSectionProps = BaseShipmentFormSectionProps &
  PropsWithChildren<
    PropsWithClassName<{
      icon?: IconName
      pillClassName?: string
      pillContents?: ReactNode
      iconClassName?: string
      title?: string
      cy?: string
      // opt-in since some pills render interactive controls (menus, edit buttons) that shouldn't also toggle the section
      pillClickable?: boolean
    }>
  >
export const ShipmentFormSection = ({
  className,
  pillClassName,
  iconClassName,
  pillContents,
  title,
  cy,
  icon,
  collapsible = false,
  defaultCollapsed = false,
  pillClickable = false,
  children,
}: ShipmentFormSectionProps) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [collapsed, toggleCollapsed] = useToggle(defaultCollapsed)
  const isCollapsed = collapsible && collapsed

  function handleToggle() {
    if (!collapsed) {
      const invalidControl = Array.from(
        sectionRef.current?.querySelectorAll<FormControl>(
          'input, select, textarea',
        ) ?? [],
      ).find(el => !el.checkValidity())

      if (invalidControl) {
        invalidControl.reportValidity()
        return
      }
    }
    toggleCollapsed()
  }

  const clickablePill = collapsible && pillClickable

  return (
    <div
      ref={sectionRef}
      className={clsx('border p-4 relative rounded-md', className, {
        'pt-8': !!title || !!pillContents,
        'pb-0': isCollapsed,
      })}
      data-cy={cy}
    >
      {(title || pillContents) && (
        <PillHeader
          className={clsx(
            pillClassName,
            clickablePill && 'cursor-pointer group !px-2',
          )}
          fixedWidthIcon={clickablePill}
          {...(clickablePill && {
            onClick: handleToggle,
            role: 'button',
            tabIndex: 0,
            ariaExpanded: !collapsed,
            onKeyDown: e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleToggle()
              }
            },
          })}
        >
          {collapsible ? (
            <FvLinkButton
              icon={collapsed ? 'chevron-right' : 'chevron-down'}
              iconClass={clsx(
                iconClassName,
                clickablePill && 'group-hover:text-fv-gray-dark',
              )}
              onClick={clickablePill ? undefined : handleToggle}
              role={clickablePill ? undefined : 'button'}
              aria-expanded={clickablePill ? undefined : !collapsed}
              title={collapsed ? 'Expand section' : 'Collapse section'}
            />
          ) : (
            icon && (
              <Icon className={iconClassName} icon={icon} transform="up-1" />
            )
          )}
          {!pillContents && title && <span>{title}</span>}
          {pillContents}
        </PillHeader>
      )}
      {!isCollapsed && children}
    </div>
  )
}
